import { ArrowRight, CircleSlash, Sparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useLocation, useParams } from "react-router-dom";

import { BackLink } from "@/components/BackLink";
import { eventBase, useBrandedRedirect } from "@/lib/microsite";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { HorseForm } from "@/components/HorseForm";
import { SubmissionTracker } from "@/components/SubmissionTracker";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { localized } from "@/lib/intl";

/*
 * C2 — "Register a horse" (consignment) flow. Guided, Apple-style:
 * criteria up front -> the EXACT same horse form organizers use (media +
 * AI pedigree included) -> submitted, with an Uber-style tracker.
 */
export default function SubmitHorsePage() {
  const { slug: legacySlug, eventSlug } = useParams();
  const slug = eventSlug || legacySlug;
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage;
  const { user } = useAuth();
  const location = useLocation();

  const [payload, setPayload] = useState(undefined);
  const [stage, setStage] = useState("intro"); // intro | form | done
  const [criteriaFail, setCriteriaFail] = useState(null);
  const [submitError, setSubmitError] = useState(null);

  useEffect(() => {
    // C2: lightweight endpoint — serves DRAFT events too (owners submit BEFORE
    // publication via the organizer's shared link)
    api
      .get(`/public/events/${slug}/submission-info`)
      .then(({ data }) => setPayload(data))
      .catch(() => setPayload(null));
  }, [slug]);

  useBrandedRedirect(payload?.organizer?.slug, payload?.event?.slug);

  if (payload === undefined) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-20 flex flex-col gap-6">
        <Skeleton className="h-8 w-2/3 rounded-xl" />
        <Skeleton className="h-40 w-full rounded-3xl" />
      </div>
    );
  }
  if (payload === null) {
    return <Navigate to={eventBase(payload?.organizer?.slug, slug)} replace />;
  }

  const event = payload.event;
  // draft auctions have no public landing yet — back-to-event links are hidden
  const isDraft = event.status === "draft";
  const settings = event.submissions || {};
  const title = localized(event.title, locale);

  const typeChips = (settings.accepted_types || []).map((type) => t(`asset.${type}`));
  const discChips = (settings.disciplines || []).map((d) => t(`disc.${d}`));
  const hasAgeRange = settings.age_min != null || settings.age_max != null;

  const checkCriteria = (horse) => {
    if (settings.accepted_types?.length && !settings.accepted_types.includes(horse.asset_type)) {
      return t("submit.typeNotAccepted", { types: typeChips.join(", ") });
    }
    if (settings.disciplines?.length && horse.disciplines?.length && !horse.disciplines.some((d) => settings.disciplines.includes(d))) {
      return t("submit.disciplineNotAccepted", { disciplines: discChips.join(", ") });
    }
    if (horse.year_of_birth && hasAgeRange) {
      const age = new Date().getFullYear() - horse.year_of_birth;
      if ((settings.age_min != null && age < settings.age_min) || (settings.age_max != null && age > settings.age_max)) {
        return t("submit.ageOutOfRange", { min: settings.age_min ?? 0, max: settings.age_max ?? 30 });
      }
    }
    return null;
  };

  const begin = async () => {
    setSubmitError(null);
    await api.post("/submissions/stable"); // idempotent personal stable
    setStage("form");
  };

  const onSaved = async (horse) => {
    const err = checkCriteria(horse); // client-side friendly guard
    if (err) {
      setCriteriaFail(err);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    try {
      await api.post(`/events/${event.id}/submissions`, { horse_id: horse.id });
      setStage("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      const detail = e.response?.data?.detail;
      const map = {
        submissions_disabled: t("submit.closedDesc"),
        type_not_accepted: t("submit.typeNotAccepted", { types: typeChips.join(", ") }),
        discipline_not_accepted: t("submit.disciplineNotAccepted", { disciplines: discChips.join(", ") }),
        age_out_of_range: t("submit.ageOutOfRange", { min: settings.age_min ?? 0, max: settings.age_max ?? 30 }),
        already_submitted: t("submit.alreadySubmitted"),
      };
      setSubmitError(map[detail] || t("auth.genericError"));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-14 lg:py-20" data-testid="submit-horse-page">
      {!isDraft && <BackLink fallback={eventBase(payload?.organizer?.slug, slug)} testid="submit-back-link" />}
      <div className="mt-8 flex flex-col gap-2 mb-10">
        <span className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#1D4D3B]">{t("submit.eyebrow")}</span>
        <h1 className="text-3xl sm:text-4xl font-medium tracking-tight text-[#0A0A0A]">{title}</h1>
        {payload.organizer?.name && <p className="text-sm text-[#737373]">{t("pub.by", { name: payload.organizer.name })}</p>}
      </div>

      {!settings.enabled ? (
        <div className="rounded-3xl border border-black/5 bg-[#F9F9F9] px-8 py-12 flex flex-col items-center text-center gap-4" data-testid="submit-closed">
          <CircleSlash className="w-8 h-8 text-[#0A0A0A]/30" aria-hidden="true" />
          <h2 className="text-lg font-medium text-[#0A0A0A]">{t("submit.closedTitle")}</h2>
          <p className="text-sm text-[#737373] max-w-sm">{t("submit.closedDesc")}</p>
          {!isDraft && (
            <Link to={eventBase(payload?.organizer?.slug, slug)} className="text-sm font-medium underline underline-offset-4" data-testid="submit-closed-back">
              {t("submit.backToEvent")}
            </Link>
          )}
        </div>
      ) : stage === "done" ? (
        <div className="rounded-3xl border border-black/5 bg-white px-8 py-12 flex flex-col gap-8" data-testid="submit-done">
          <div className="flex flex-col items-center text-center gap-3">
            <span className="w-14 h-14 rounded-full bg-[#1D4D3B]/8 border border-[#1D4D3B]/15 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-[#1D4D3B]" aria-hidden="true" />
            </span>
            <h2 className="text-2xl font-medium tracking-tight text-[#0A0A0A]">{t("submit.doneTitle")}</h2>
            <p className="text-sm text-[#737373] max-w-md">{t("submit.doneDesc")}</p>
          </div>
          <div className="max-w-md w-full mx-auto">
            <SubmissionTracker status="submitted" />
          </div>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/account/submissions"
              data-testid="submit-track-link"
              className="inline-flex items-center gap-2 rounded-full bg-[#0A0A0A] text-white text-sm font-medium px-7 h-11 hover:bg-black/80 transition-colors duration-300"
            >
              {t("submit.trackCta")}
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
            {!isDraft && (
              <Link to={`/e/${slug}`} className="text-sm font-medium text-[#0A0A0A]/60 hover:text-[#0A0A0A] transition-colors duration-200" data-testid="submit-done-back">
                {t("submit.backToEvent")}
              </Link>
            )}
          </div>
        </div>
      ) : !user ? (
        <div className="rounded-3xl border border-black/5 bg-white px-8 py-12 flex flex-col items-center text-center gap-5" data-testid="submit-signin">
          <h2 className="text-lg font-medium text-[#0A0A0A]">{t("submit.signinTitle")}</h2>
          <p className="text-sm text-[#737373] max-w-sm">{t("submit.signinDesc")}</p>
          <Link
            to={`/signin?next=${encodeURIComponent(location.pathname)}`}
            data-testid="submit-signin-link"
            className="inline-flex items-center justify-center rounded-full bg-[#0A0A0A] text-white text-sm font-medium px-7 h-11 hover:bg-black/80 transition-colors duration-300"
          >
            {t("gate.signin")}
          </Link>
        </div>
      ) : stage === "intro" ? (
        <div className="rounded-3xl border border-black/5 bg-white px-8 py-10 flex flex-col gap-8" data-testid="submit-intro">
          <div className="flex flex-col gap-2">
            <h2 className="text-lg font-medium text-[#0A0A0A]">{t("submit.introTitle")}</h2>
            <p className="text-sm text-[#737373]">{t("submit.introDesc")}</p>
          </div>
          <div className="flex flex-col gap-3">
            <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#0A0A0A]/40">{t("submit.criteriaTitle")}</span>
            <div className="flex flex-wrap gap-2" data-testid="submit-criteria-chips">
              {typeChips.length ? (
                typeChips.map((c) => (
                  <span key={c} className="rounded-full bg-[#1D4D3B]/8 border border-[#1D4D3B]/15 text-[#1D4D3B] text-xs font-medium px-3.5 py-1.5">
                    {c}
                  </span>
                ))
              ) : (
                <span className="rounded-full bg-black/5 text-[#0A0A0A] text-xs font-medium px-3.5 py-1.5">{t("submit.anyType")}</span>
              )}
              {hasAgeRange && (
                <span className="rounded-full bg-black/5 text-[#0A0A0A] text-xs font-medium px-3.5 py-1.5" data-testid="submit-age-chip">
                  {t("submit.ageChip", { min: settings.age_min ?? 0, max: settings.age_max ?? 30 })}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={begin}
            data-testid="submit-begin-button"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#0A0A0A] text-white text-sm font-medium px-8 h-12 hover:bg-black/80 transition-colors duration-300 w-fit"
          >
            {t("common.continue")}
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {(criteriaFail || submitError) && (
            <p role="alert" data-testid="submit-criteria-error" className="text-sm text-amber-800 bg-amber-500/[0.08] border border-amber-500/25 rounded-2xl px-5 py-4">
              {criteriaFail || submitError}
            </p>
          )}
          <div className="rounded-3xl border border-black/5 bg-white p-6 sm:p-8">
            <ErrorBoundary name="submit-horse-form">
              <HorseForm onSaved={onSaved} onCancel={() => setStage("intro")} />
            </ErrorBoundary>
          </div>
        </div>
      )}
    </div>
  );
}
